import { FC, Fragment, useCallback, useMemo } from 'react'
import { StageOrder, compareAssignmentStageOrder } from 'utils/stages'

import AssignmentStageAdvice from 'components/common/AssignmentStageAdvice/AssignmentStageAdvice'
import CheckIcon from '@mui/icons-material/Check'
import { MUIButton } from 'components/common/MUIButton'
import { PIPEDRIVE_INFINITY } from 'constants/pipedrive'
import { Stack } from '@mui/material'
import { useActionPopup } from 'utils/hooks'
import { useGalleryAssignment } from 'components/pages/Gallery/_main/contexts/GalleryAssignment.context'
import { useStagesUnlockingSendToClient } from 'components/pages/Gallery/_main/contexts/useStagesUnlockingSendToClient'
import { useSendVisualsToClient } from 'dataQueries'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'components/contexts/SnackbarService.context'

interface Props {
  /** If we allow sending assignment to the client, based on external condition */
  canSendAssignmentVisualsToClient: Boolean,
  /** Assignment ID, which should be sent out */
  assignmentId: string,
  /** Count of visuals in the assignment */
  visualsCount: number,
  /** Maximal number of selected visuals for an assignment*/
  selectMaxValue: number,
}

export const SendVisualsToClient: FC<Props> = ({
  canSendAssignmentVisualsToClient,
  assignmentId,
  visualsCount,
  selectMaxValue,
}) => {
  const { t } = useTranslation(['gallery'])
  const { showConfirm } = useActionPopup()
  const stagesUnlockingSendAssignmentVisualsToClient = useStagesUnlockingSendToClient()
  const { assignmentStage } = useGalleryAssignment()
  const sendAssignmentVisualsToClient = useSendVisualsToClient()
  const { spawnSuccessToast } = useSnackbar()

  const canBeSendToClient = useMemo(() => {
    return canSendAssignmentVisualsToClient && (!sendAssignmentVisualsToClient || sendAssignmentVisualsToClient.isIdle)
  }, [canSendAssignmentVisualsToClient, sendAssignmentVisualsToClient])
  const assignmentVisualsAlreadySentToClient = useMemo(() => (!!assignmentStage && (compareAssignmentStageOrder(assignmentStage, stagesUnlockingSendAssignmentVisualsToClient) === StageOrder.COMPARING_AFTER_COMPARED_TO)), [assignmentStage, stagesUnlockingSendAssignmentVisualsToClient])
  const cannotSendAssignmentVisualsToClientInfo = useMemo(() => {
    if ((!(!sendAssignmentVisualsToClient || sendAssignmentVisualsToClient.isIdle)) || assignmentVisualsAlreadySentToClient) {
      return t('sent_to_client')
    } else if (!assignmentStage || !stagesUnlockingSendAssignmentVisualsToClient.has(assignmentStage)) {
      return <AssignmentStageAdvice id="send_to_client" currentStage={assignmentStage} validStages={stagesUnlockingSendAssignmentVisualsToClient} />
    } else if (!(visualsCount >= selectMaxValue || selectMaxValue === PIPEDRIVE_INFINITY)) {
      return t('select_more_visuals')
    } else {
      return ''
    }
  }, [assignmentStage, assignmentVisualsAlreadySentToClient, selectMaxValue, sendAssignmentVisualsToClient, stagesUnlockingSendAssignmentVisualsToClient, t, visualsCount])

  const initiateSendAssignmentVisualsToClient = useCallback(async () => {
    if (!canSendAssignmentVisualsToClient) return
    if (!(await showConfirm(t('send_to_client_prompt')))) return
    sendAssignmentVisualsToClient.mutate(
      { assignmentId },
      {
        onSuccess: () => {
          spawnSuccessToast(t('sent_to_client'))
        },
      }
    )
  }, [assignmentId, canSendAssignmentVisualsToClient, sendAssignmentVisualsToClient, showConfirm, spawnSuccessToast, t])

  return (
    <Fragment>
      <Stack alignItems="center" direction="row" flexWrap="wrap" mb="2rem" gap={1}>
        <MUIButton
          startIcon={<CheckIcon />}
          type="greenPrimary"
          disabled={!canBeSendToClient}
          onClick={initiateSendAssignmentVisualsToClient}
        >
          <span>{t('send_to_client')}</span>
        </MUIButton>

        {!canBeSendToClient && cannotSendAssignmentVisualsToClientInfo &&
          <span>{cannotSendAssignmentVisualsToClientInfo}</span>
        }
      </Stack>
    </Fragment>
  )
}
